<template>
  <div ref="host" class="skp-viewer" :style="{ height: height + 'px' }">
    <div class="skp-viewer__toolbar">
      <button
        type="button"
        title="Orbit"
        :class="{ active: tool === 'orbit' }"
        @click="setTool('orbit')"
      >⟳</button>
      <button
        type="button"
        title="Pan"
        :class="{ active: tool === 'pan' }"
        @click="setTool('pan')"
      >✥</button>
      <button type="button" title="Zoom Extents" @click="zoomExtents">⤢</button>
      <button type="button" title="Reset View" @click="resetView">⌂</button>
      <button
        type="button"
        title="Axes"
        :class="{ active: showAxes }"
        @click="toggleAxes"
      >✕</button>
      <button
        type="button"
        title="Shadows"
        :class="{ active: showShadows }"
        @click="toggleShadows"
      >☀</button>
    </div>
    <div v-if="loading" class="skp-viewer__status">3D wird geladen…</div>
    <div v-else-if="error" class="skp-viewer__status skp-viewer__status--error">{{ error }}</div>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as THREE from 'three'
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'

const BACKGROUND = 0xe8eaed

function resolveSrc (src) {
  if (!src) {
    return ''
  }
  let value = String(src).trim()
  if (!value) {
    return ''
  }
  if (!/^https?:\/\//i.test(value) && !value.startsWith('/')) {
    value = '/' + value.replace(/^\.?\//, '')
  }
  if (/\.skp($|\?)/i.test(value)) {
    value = value.replace(/\.skp(?=$|\?)/i, '.glb')
  }
  if (/cdn\.loewe-zaun\.de|digitaloceanspaces\.com/i.test(value)) {
    const match = value.match(/\/models\/([^/?#]+)/i)
    const apiUrl = String(process.env.VUE_APP_API_URL || '').replace(/\/?$/, '/')
    if (match && apiUrl) {
      return apiUrl + 'models/' + encodeURIComponent(match[1])
    }
  }
  return value
}

export default {
  name: 'ModelViewer3d',
  props: {
    src: {
      type: String,
      required: true
    },
    width: {
      type: Number,
      default: 950
    },
    height: {
      type: Number,
      default: 626
    }
  },
  setup (props) {
    const host = ref(null)
    const loading = ref(true)
    const error = ref('')
    const tool = ref('orbit')
    const showAxes = ref(true)
    const showShadows = ref(true)

    let renderer
    let scene
    let camera
    let controls
    let animationId
    let resizeObserver
    let pmremGenerator
    let currentModel
    let axesHelper
    let gridHelper
    let keyLight
    let defaultCameraPos
    let defaultTarget

    function disposeObject (root) {
      root.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose()
        }
        if (object.material) {
          const materials = Array.isArray(object.material) ? object.material : [object.material]
          materials.forEach((material) => {
            Object.keys(material).forEach((key) => {
              const value = material[key]
              if (value && value.isTexture) {
                value.dispose()
              }
            })
            material.dispose()
          })
        }
      })
    }

    function clearModel () {
      if (!scene || !currentModel) {
        return
      }
      scene.remove(currentModel)
      disposeObject(currentModel)
      currentModel = null
    }

    function enhanceMaterials (root) {
      root.traverse((object) => {
        if (object.isLine || object.isLineSegments || object.isLineLoop) {
          object.visible = false
          return
        }
        if (!object.isMesh || !object.material) {
          return
        }
        const materials = Array.isArray(object.material) ? object.material : [object.material]
        materials.forEach((material) => {
          if (material.map) {
            material.map.colorSpace = THREE.SRGBColorSpace
          }
          if (material.isMeshStandardMaterial || material.isMeshPhysicalMaterial) {
            const color = material.color
            const isBright = color && color.r > 0.85 && color.g > 0.85 && color.b > 0.85
            if (isBright && !material.map) {
              color.setHex(0x4a4f57)
            }
            material.metalness = Math.max(material.metalness || 0, 0.65)
            material.roughness = Math.min(Math.max(material.roughness ?? 0.4, 0.28), 0.5)
            material.envMapIntensity = 1
            material.needsUpdate = true
          }
          object.castShadow = true
          object.receiveShadow = true
        })
      })
    }

    function placeOnGround (object) {
      const box = new THREE.Box3().setFromObject(object)
      const center = box.getCenter(new THREE.Vector3())
      object.position.x -= center.x
      object.position.z -= center.z
      object.position.y -= box.min.y
      object.updateMatrixWorld(true)
    }

    function fitCameraToObject (object) {
      const box = new THREE.Box3().setFromObject(object)
      const size = box.getSize(new THREE.Vector3())
      const center = box.getCenter(new THREE.Vector3())
      const maxSize = Math.max(size.x, size.y, size.z) || 1
      const distance = maxSize * 1.45

      camera.near = Math.max(distance / 200, 0.01)
      camera.far = distance * 120
      camera.position.set(
        center.x + distance * 0.9,
        center.y + distance * 0.42,
        center.z + distance * 0.9
      )
      camera.lookAt(center.x, center.y * 0.55, center.z)
      camera.updateProjectionMatrix()

      controls.target.set(center.x, center.y * 0.55, center.z)
      controls.minDistance = maxSize * 0.15
      controls.maxDistance = maxSize * 10
      controls.update()

      defaultCameraPos = camera.position.clone()
      defaultTarget = controls.target.clone()

      const gridSize = Math.max(maxSize * 2.5, 20)
      if (gridHelper) {
        scene.remove(gridHelper)
        gridHelper.geometry.dispose()
        gridHelper.material.dispose()
      }
      gridHelper = new THREE.GridHelper(gridSize, 40, 0xb8bec6, 0xd0d5db)
      gridHelper.position.y = 0.002
      scene.add(gridHelper)

      if (axesHelper) {
        axesHelper.scale.setScalar(Math.max(maxSize * 0.35, 1))
      }
    }

    function zoomExtents () {
      if (currentModel) {
        fitCameraToObject(currentModel)
      }
    }

    function resetView () {
      if (!defaultCameraPos || !defaultTarget) {
        zoomExtents()
        return
      }
      camera.position.copy(defaultCameraPos)
      controls.target.copy(defaultTarget)
      controls.update()
    }

    function setTool (next) {
      tool.value = next
      if (!controls) {
        return
      }
      if (next === 'pan') {
        controls.enableRotate = false
        controls.enablePan = true
        controls.mouseButtons.LEFT = THREE.MOUSE.PAN
      } else {
        controls.enableRotate = true
        controls.enablePan = true
        controls.mouseButtons.LEFT = THREE.MOUSE.ROTATE
      }
    }

    function toggleAxes () {
      showAxes.value = !showAxes.value
      if (axesHelper) {
        axesHelper.visible = showAxes.value
      }
    }

    function toggleShadows () {
      showShadows.value = !showShadows.value
      if (renderer) {
        renderer.shadowMap.enabled = showShadows.value
      }
      if (keyLight) {
        keyLight.castShadow = showShadows.value
      }
    }

    function onModelReady (model) {
      enhanceMaterials(model)
      model.updateMatrixWorld(true)
      placeOnGround(model)
      model.userData.isModelRoot = true
      scene.add(model)
      currentModel = model
      fitCameraToObject(model)
      loading.value = false
    }

    function loadModel () {
      if (!scene) {
        return
      }
      const url = resolveSrc(props.src)
      if (!url) {
        loading.value = false
        error.value = '3D-Modell fehlt.'
        return
      }

      loading.value = true
      error.value = ''
      clearModel()

      const basePath = url.replace(/[^/]+$/, '')
      const isGltf = /\.glb($|\?)/i.test(url) || /\.gltf($|\?)/i.test(url)
      const loader = isGltf ? new GLTFLoader() : new ColladaLoader()
      if (loader.setResourcePath) {
        loader.setResourcePath(basePath)
      }

      loader.load(
        url,
        (result) => {
          onModelReady(result.scene)
        },
        undefined,
        (err) => {
          console.error(err)
          loading.value = false
          error.value = '3D-Modell konnte nicht geladen werden.'
        }
      )
    }

    function animate () {
      animationId = window.requestAnimationFrame(animate)
      controls?.update()
      renderer?.render(scene, camera)
    }

    function setSize () {
      if (!host.value || !renderer || !camera) {
        return
      }
      const width = host.value.clientWidth || props.width
      const height = host.value.clientHeight || props.height
      renderer.setSize(width, height, false)
      camera.aspect = width / height
      camera.updateProjectionMatrix()
    }

    function init () {
      if (!host.value) {
        return
      }

      scene = new THREE.Scene()
      scene.background = new THREE.Color(BACKGROUND)

      camera = new THREE.PerspectiveCamera(40, props.width / props.height, 0.1, 5000)
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.setSize(props.width, props.height)
      renderer.outputColorSpace = THREE.SRGBColorSpace
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = 0.95
      renderer.shadowMap.enabled = true
      renderer.shadowMap.type = THREE.PCFSoftShadowMap
      host.value.appendChild(renderer.domElement)

      pmremGenerator = new THREE.PMREMGenerator(renderer)
      scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture

      controls = new OrbitControls(camera, renderer.domElement)
      controls.enableDamping = true
      controls.dampingFactor = 0.08
      controls.screenSpacePanning = true
      controls.maxPolarAngle = Math.PI * 0.495
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.DOLLY,
        RIGHT: THREE.MOUSE.PAN
      }

      const hemi = new THREE.HemisphereLight(0xffffff, 0x9aa0a6, 0.6)
      keyLight = new THREE.DirectionalLight(0xffffff, 0.85)
      keyLight.position.set(10, 16, 8)
      keyLight.castShadow = true
      keyLight.shadow.mapSize.set(2048, 2048)
      keyLight.shadow.camera.near = 0.5
      keyLight.shadow.camera.far = 120
      keyLight.shadow.camera.left = -40
      keyLight.shadow.camera.right = 40
      keyLight.shadow.camera.top = 40
      keyLight.shadow.camera.bottom = -40
      const fill = new THREE.DirectionalLight(0xe8eef8, 0.35)
      fill.position.set(-10, 8, -6)
      scene.add(hemi, keyLight, fill)

      const ground = new THREE.Mesh(
        new THREE.CircleGeometry(120, 64),
        new THREE.MeshStandardMaterial({
          color: 0xd8dce0,
          metalness: 0,
          roughness: 1
        })
      )
      ground.rotation.x = -Math.PI / 2
      ground.receiveShadow = true
      scene.add(ground)

      axesHelper = new THREE.AxesHelper(2)
      axesHelper.visible = showAxes.value
      scene.add(axesHelper)

      resizeObserver = new ResizeObserver(() => setSize())
      resizeObserver.observe(host.value)

      loadModel()
      animate()
    }

    function destroy () {
      if (animationId) {
        window.cancelAnimationFrame(animationId)
      }
      resizeObserver?.disconnect()
      clearModel()
      controls?.dispose()
      pmremGenerator?.dispose()
      renderer?.dispose()
      if (renderer?.domElement?.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement)
      }
      renderer = null
      scene = null
      camera = null
      controls = null
      pmremGenerator = null
      keyLight = null
      axesHelper = null
      gridHelper = null
    }

    onMounted(() => {
      init()
    })

    onBeforeUnmount(() => {
      destroy()
    })

    watch(() => props.src, () => {
      loadModel()
    })

    return {
      host,
      loading,
      error,
      tool,
      showAxes,
      showShadows,
      setTool,
      zoomExtents,
      resetView,
      toggleAxes,
      toggleShadows
    }
  }
}
</script>

<style scoped>
.skp-viewer {
  position: relative;
  width: 100%;
  background: #e8eaed;
  overflow: hidden;
  user-select: none;
}

.skp-viewer canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.skp-viewer__toolbar {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skp-viewer__toolbar button {
  width: 36px;
  height: 36px;
  border: 1px solid #b8bec6;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.95);
  color: #222;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}

.skp-viewer__toolbar button:hover,
.skp-viewer__toolbar button.active {
  background: #fff;
  border-color: #2b8ff2;
  color: #2b8ff2;
}

.skp-viewer__status {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  font-size: 14px;
  color: #444;
  background: rgba(232, 234, 237, 0.78);
}

.skp-viewer__status--error {
  color: #b00020;
}
</style>
